// ==========================================================================
// usePreAlertSurface - Drawer/banner visibility state
//
// Behaviour:
//   - The CTA card (banner) appears automatically when no active alert is
//     present and there are pre-alert actions available — it's the
//     persistent home-screen entry point.
//   - The bottom drawer ONLY opens when the user taps "Explore how" on the
//     CTA card. It NEVER auto-opens on app launch — that was previously
//     confusing users.
//   - Both the drawer and the banner are hidden entirely when an active
//     alert is present.
// ==========================================================================

import { useCallback, useEffect, useState } from 'react';

interface UsePreAlertSurfaceOptions {
  hasAlert: boolean;
  hasActions: boolean;
}

export function usePreAlertSurface({ hasAlert, hasActions }: UsePreAlertSurfaceOptions) {
  const [drawerOpen, setDrawerOpen] = useState(false);

  // Force-close the drawer if an alert appears while it's open.
  useEffect(() => {
    if (hasAlert) {
      setDrawerOpen(false);
    }
  }, [hasAlert]);

  const openDrawer = useCallback(() => {
    setDrawerOpen(true);
  }, []);

  const closeDrawer = useCallback(() => {
    setDrawerOpen(false);
  }, []);

  // Banner stays rendered even while the drawer is open — the drawer
  // overlays it visually, but keeping the banner in the DOM means no
  // flicker when the user dismisses the drawer.
  const showBanner = !hasAlert && hasActions;

  return {
    drawerOpen,
    showBanner,
    openDrawer,
    closeDrawer,
  };
}
